import React, { useEffect, useState } from "react";
import { Candidate } from "../../interfaces/candidate";
import { useAuth } from "../../auth";
import {
  alertOfInactiveClient,
  getCandidateByEmail,
} from "../../services/candidateService";
import {
  checkOrderingAvailability,
  createOrderCandidate,
  getAllOrdersByCandidateID,
  updateOrderPaymentStatus,
  updateOrderStatus,
} from "../../services/orderService";
import { CandidateCreateOrder, Order } from "../../interfaces/order";
import {
  getOrderFormsByOrderID,
  getOrderFormTypes,
} from "../../services/orderFormService";
import { OrderForm } from "../../interfaces/orderForm";
import { useLocation, useNavigate } from "react-router-dom";
import { SimpleClient } from "../../interfaces/client";
import {
  checkClientInfo,
  checkClientStatusByAbbreviation,
  checkForms,
  getSimpleClient,
  getSimpleClientByAbbreviation,
} from "../../services/clientService";
import PaymentForm from "../../components/PaymentForm";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import {
  createCandidatePayment,
  getCandidatePayment,
  processPaymentIntent,
  updateCandidatePayment,
} from "../../services/paymentService";
import { getReferenceOrderByOrderID } from "../../services/referenceOrderService";
import ToolTip from "../../components/component-tools/ToolTip";
import { sendNotifyClientEmail } from "../../services/emailService";
import { NotificationType } from "../../interfaces/clientNotification";

const NeedsReview = () => {
  const [loading, setLoading] = useState(true);
  const { decodeToken } = useAuth();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderForms, setOrderForms] = useState<{ [key: number]: OrderForm[] }>(
    {},
  );
  const [clients, setClients] = useState<{ [key: number]: SimpleClient }>({});
  const [orderFormTypes, setOrderFormTypes] = useState<{
    [key: number]: string;
  }>({});
  const navigate = useNavigate();
  const location = useLocation();
  let successMessageState = location.state?.successMessage;
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [candidateOrder, setCandidateOrder] = useState<CandidateCreateOrder>();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const key =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_STRIPE_TEST_KEY
      : process.env.REACT_APP_STRIPE_KEY;
  const stripePromise = loadStripe(key || "");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const stripe = useStripe();
  const [showNewOrderButton, setShowNewOrderButton] = useState(false);
  const [anyCompletedForms, setAnyCompletedForms] = useState(false);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const email = decodeToken(localStorage.getItem("token") || "");
        const candidate = await getCandidateByEmail(email);
        setCandidate(candidate);

        const ordersResponse = await getAllOrdersByCandidateID(
          candidate.candidateID,
        );

        // if there are any orders in orderResponse AND all of them have a status of 4 or 5 then TRUE
        const showNewOrderButton =
          ordersResponse.length > 0 &&
          ordersResponse.every(
            (order) =>
              order.orderStatusID === 4 ||
              order.orderStatusID === 5 ||
              order.orderStatusID === 6,
          );
        setShowNewOrderButton(showNewOrderButton);

        const filteredOrders = ordersResponse.filter(
          (order) => order.orderStatusID === 1,
        );
        setOrders(filteredOrders);

        for (const order of filteredOrders) {
          const formsResponse = await getOrderFormsByOrderID(order.orderID);
          if (formsResponse) {
            const visibleForms = formsResponse.filter(
              (form) => form.isVisible === true,
            );
            const orderFormTypeOrder = [1, 2, 3, 4, 5, 6, 7, 9, 8];

            visibleForms.sort(
              (a, b) =>
                orderFormTypeOrder.indexOf(a.orderFormTypeID) -
                orderFormTypeOrder.indexOf(b.orderFormTypeID),
            );

            setAnyCompletedForms(
              visibleForms.some(
                (f) =>
                  (f.completedFormUrl ?? "").trim() !== "" &&
                  [2, 3, 4, 7, 9].includes(f.orderFormTypeID),
              ),
            );

            setOrderForms((prevForms) => ({
              ...prevForms,
              [order.orderID]: visibleForms || [],
            }));
          }

          if (!clients[order.clientID]) {
            const simpleClient = await getSimpleClient(order.clientID);
            if (simpleClient) {
              setClients((prev) => ({
                ...prev,
                [order.clientID]: simpleClient,
              }));
            }
          }
        }

        const formTypes = await getOrderFormTypes();
        if (formTypes) {
          const formTypeDescriptions = formTypes.reduce(
            (acc, formType) => ({
              ...acc,
              [formType.orderFormTypeID]: formType.description,
            }),
            {},
          );
          setOrderFormTypes(formTypeDescriptions);
        }

        if (successMessageState) {
          setSuccessMessage(successMessageState);
        }
      } catch (error) {
        console.error("Error loading orders, forms, or clients:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [clients, decodeToken, successMessageState]);

  const handlePayment = async (orderID: number) => {
    const order = orders.find((order) => order.orderID === orderID);
    if (order && order.requiresCandidatePayment && !order.paymentReceived) {
      try {
        setLoadingSpinner(true);
        if (candidate) {
          const paymentRecord = await createCandidatePayment({
            paymentID: 0,
            orderID: order.orderID,
            paymentIntentID: "xxxxxx",
            paymentMethod: "card",
            amount: 175,
            currency: "usd",
            paymentSuccess: false,
            paymentDate: new Date(),
          });
          const response = await processPaymentIntent(
            175,
            paymentRecord.paymentID,
            candidate?.candidateEmail,
          );
          localStorage.setItem("paymentID", paymentRecord.paymentID.toString());
          setClientSecret(response.clientSecret);
          setLoadingSpinner(false);
          setShowPaymentModal(true);
        }
      } catch (error: any) {
        console.log(error);
      }
      return;
    } else {
      console.error("order not found.");
    }
  };

  //check for success or failure of payment
  useEffect(() => {
    if (!stripe) return;

    const clientSecret = new URLSearchParams(location.search).get(
      "payment_intent_client_secret",
    );
    const orderIDStr = new URLSearchParams(location.search).get("orderID");
    const orderID = orderIDStr ? parseInt(orderIDStr, 10) : NaN;
    if (!clientSecret) return;

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(async ({ paymentIntent }) => {
        if (!paymentIntent) return;
        if (paymentIntent.status === "succeeded" && orderID) {
          const updatedOrder = await updateOrderPaymentStatus(orderID, true);
          setOrders((prevOrders) =>
            prevOrders.map((order) =>
              order.orderID === orderID ? updatedOrder : order,
            ),
          );

          try {
            const paymentIDStr = localStorage.getItem("paymentID");
            const paymentID = paymentIDStr ? parseInt(paymentIDStr, 10) : NaN;
            const existingCandidatePayment =
              await getCandidatePayment(paymentID);
            if (existingCandidatePayment) {
              const updatedCandidatePayment = {
                ...existingCandidatePayment,
                PaymentIntentID: paymentIntent.id,
                PaymentSuccess: true,
                PaymentDate: new Date(),
              };
              await updateCandidatePayment(updatedCandidatePayment);
            }
          } catch (error) {
            console.error("Error retrieving candidate payment record:", error);
          }

          setSuccessMessage("Payment succeeded! Please submit your order.");
        } else setErrorMessage("Payment failed. Please contact support.");
      });
  }, [stripe, location.search, location.pathname, navigate]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, location.pathname, navigate]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleStartForm = (orderID: number, form: OrderForm) => {
    const { orderFormTypeID, orderFormID } = form;

    const routes: { [key: number]: string } = {
      1: "/candidate/forms/background-check-auth-fcra",
      2: "/candidate/forms/criminal-records-auth",
      3: "/candidate/forms/credit-report-auth",
      4: "/candidate/forms/motor-vehicle-auth",
      5: "/candidate/forms/reference-screening-auth",
      6: "/candidate/forms/screening-questionnaire",
      7: "/candidate/forms/reference-identification",
      8: "/candidate/forms/transfer-release",
      9: "/candidate/forms/social-media-auth",
    };

    const route = routes[orderFormTypeID];
    if (route) {
      navigate(`${route}`, {
        state: { orderID, orderFormID, anyCompletedForms },
      });
    } else {
      console.error(`No route defined for OrderFormType ${orderFormTypeID}`);
    }
  };

  const handleFinalSubmit = async (orderID: number) => {
    try {
      setLoadingSpinner(true);
      await updateOrderStatus(orderID, 2);
      setOrders((prevOrders) =>
        prevOrders.filter((order) => order.orderID !== orderID),
      );

      setLoadingSpinner(false);
      const response = await getReferenceOrderByOrderID(orderID);
      if (response.referenceOrderID === 0) {
        setSuccessMessage("Order submitted successfully.");
      } else {
        setSuccessMessage(
          "Thank you for submitting your background check. Please allow 1-2 business days for processing and for initial requests to be sent to your contacts provided on the Identification Form. You can log back in to track response status under the Reference Status tab. Reaching out to your contacts to encourage a response may help expedite the process.",
        );
      }

      // Send client a notification email
      const orderFound = orders.find((order) => order.orderID === orderID);
      if (orderFound) {
        await sendNotifyClientEmail({
          orderId: orderFound.orderID,
          type: NotificationType.Order,
        });
      }
    } catch (error) {
      console.error(`Error submitting order ${orderID}:`, error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
  ) => {
    setCandidateOrder((prevCandidateOrder) => ({
      ...prevCandidateOrder!,
      [field]: e.target.value,
    }));

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    if (!candidateOrder?.abbreviation)
      errors.abbreviation = "Client Number/Abbreviation is required.";
    if (!candidateOrder?.positionCode)
      errors.positionCode = "Position Code is required.";
    if (!candidateOrder?.candidateFirstName)
      errors.candidateFirstName = "First name is required.";
    if (!candidateOrder?.candidateLastName)
      errors.candidateLastName = "Last name is required.";

    if (candidateOrder?.abbreviation && candidateOrder.positionCode) {
      // check client status and check if client has necessary forms unloaded
      const clientStatus = await checkClientStatusByAbbreviation(
        candidateOrder?.abbreviation,
      );
      const client = await getSimpleClientByAbbreviation(
        candidateOrder.abbreviation,
      );

      if (client == null) {
        errors.abbreviation = "Abbreviation not found.";
      } else {
        const [formsHere] = await checkForms(client.clientID);
        const exists: boolean = await checkClientInfo(
          candidateOrder.abbreviation,
          candidateOrder.positionCode,
        );

        if (!clientStatus) {
          await alertInactiveClient(true);
          errors.abbreviation = "Client is not active.";
        } else if (!formsHere) {
          await alertInactiveClient(true);
          errors.abbreviation = "Client is not active.";
        } else if (!exists) {
          await alertInactiveClient(false);
          errors.abbreviation = "Client information combination not found.";
          errors.positionCode = "   ";
        } else {
          delete errors.abbreviation;
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoadingSpinner(true);
    try {
      if (candidateOrder && (await validateFields()) && candidate) {
        const isAvailable = await checkOrderingAvailability(
          candidateOrder.abbreviation,
          candidate.candidateID,
        );

        if (!isAvailable) {
          setValidationErrors({
            abbreviation: "You cannot create another order at this time",
          });
          return;
        }

        candidateOrder.candidateID = candidate.candidateID;
        candidateOrder.candidateEmail = candidate.candidateEmail;

        await createOrderCandidate(candidateOrder);
        setSuccessMessage("Order created successfully.");

        candidateOrder.abbreviation = "";
        candidateOrder.positionCode = 0;
        candidateOrder.candidateFirstName = "";
        candidateOrder.candidateLastName = "";

        window.location.reload();
        setShowNewOrder(false);
      }
    } catch (error) {
      console.error("Error creating order:", error);
      setValidationErrors({
        abbreviation: "Error creating order.",
      });
    } finally {
      setLoadingSpinner(false);
    }
  };

  const alertInactiveClient = async (sendToClient: boolean) => {
    try {
      setLoading(true);
      if (!candidateOrder) return;

      await alertOfInactiveClient(candidateOrder, sendToClient);
    } catch (error) {
      console.error("Error notifying client and admin:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>
          {orders.length > 0 && (
            <span style={{ fontSize: "xxx-large" }}>
              <b>
                {orders[0].candidateFirstName} {orders[0].candidateLastName}
              </b>
            </span>
          )}
          <span style={{ fontSize: "xx-large" }}>
            {orders.length > 0 ? " – Needs Review" : "Needs Review"}
          </span>
        </h1>

        {showNewOrderButton && (
          <div className="page-items-container">
            <div>
              <button
                className={`oxbtn btn-new`}
                onClick={() => setShowNewOrder(!showNewOrder)}
              >
                <div> Start New Order </div>
                <i className="bi bi-plus-circle-fill plus-icon"></i>
              </button>
            </div>
            <ToolTip
              tooltipText="If you are a returning candidate or need to initiate another background check, please click here to enter your client number and position code."
              placement="left"
              className="info-icon-spec"
            ></ToolTip>
          </div>
        )}
      </div>

      {/* New Order Popup */}
      {showNewOrder && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowNewOrder(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowNewOrder(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Start a New Order</h2>
            </div>
            <div className="filter-body-data">
              <div
                className="text-sub-sub-header"
                style={{ padding: "0px", flex: "0 0 100%" }}
              >
                Your Information:
              </div>
              <div className="field-stacked" style={{ flex: "0 0 45%" }}>
                <label className="field-label">First Name:</label>
                <div className="field-input">
                  <input
                    className={`form-control ${
                      validationErrors.candidateFirstName ? "is-invalid" : ""
                    }`}
                    type="text"
                    value={candidateOrder?.candidateFirstName || ""}
                    onChange={(e) => handleInputChange(e, "candidateFirstName")}
                  />
                  {validationErrors.candidateFirstName && (
                    <div className="invalid-feedback">
                      {validationErrors.candidateFirstName}
                    </div>
                  )}
                </div>
              </div>
              <div className="field-stacked" style={{ flex: "0 0 45%" }}>
                <label className="field-label">Last Name:</label>
                <div className="field-input">
                  <input
                    className={`form-control ${
                      validationErrors.candidateLastName ? "is-invalid" : ""
                    }`}
                    type="text"
                    value={candidateOrder?.candidateLastName || ""}
                    onChange={(e) => handleInputChange(e, "candidateLastName")}
                  />
                  {validationErrors.candidateLastName && (
                    <div className="invalid-feedback">
                      {validationErrors.candidateLastName}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ flex: "0 0 100%" }}></div>
              <div
                className="text-sub-sub-header"
                style={{ padding: "0px", flex: "0 0 100%" }}
              >
                Client Information:
              </div>
              <div>
                The requesting organization is responsible for providing the
                client number/abbreviation and position code to you.
              </div>
              <div className="field-stacked" style={{ flex: "0 0 45%" }}>
                <label className="field-label">
                  Client Number/Abbreviation:
                </label>
                <div className="field-input">
                  <input
                    className={`form-control ${
                      validationErrors.abbreviation ? "is-invalid" : ""
                    }`}
                    type="text"
                    value={candidateOrder?.abbreviation || ""}
                    onChange={(e) => handleInputChange(e, "abbreviation")}
                  />
                  {validationErrors.abbreviation && (
                    <div className="invalid-feedback">
                      {validationErrors.abbreviation}
                    </div>
                  )}
                </div>
              </div>
              <div className="field-stacked" style={{ flex: "0 0 45%" }}>
                <label className="field-label">Position Code:</label>
                <div className="field-input">
                  <input
                    className={`form-control ${
                      validationErrors.positionCode ? "is-invalid" : ""
                    }`}
                    type="text"
                    value={candidateOrder?.positionCode || ""}
                    onChange={(e) => handleInputChange(e, "positionCode")}
                  />
                  {validationErrors.positionCode && (
                    <div className="invalid-feedback">
                      {validationErrors.positionCode}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="oxbtn btn-green" onClick={() => handleSave()}>
                Start
              </button>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-container">
            <div className="text-body">
              Please complete the below forms by clicking the start button. Once
              all the forms for a client are complete, the background check
              process will start. If you have any questions, please contact us.{" "}
              <span style={{ color: "red" }}>
                <b>
                  The system has a secure timeout feature that will log you out
                  after three hours.
                </b>
              </span>
            </div>
          </div>
          <div className="divider-small"></div>
          {successMessage && (
            <div className="alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert-message alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
            </div>
          )}
          <br></br>
          <div className="orders-container">
            {orders.length > 0 ? (
              <div className="orders-list" style={{ width: "100%" }}>
                {orders.map((order, index) => {
                  const allFormsCompleted = orderForms[order.orderID]?.every(
                    (form) => form.completedFormUrl,
                  );
                  const needsToPay =
                    order.requiresCandidatePayment && !order.paymentReceived;

                  const questionnaireForm = orderForms[order.orderID]?.find(
                    (form) => form.orderFormTypeID === 6,
                  );

                  const showNotaryRequirement =
                    questionnaireForm?.notaryRequired &&
                    questionnaireForm?.completedFormUrl &&
                    !questionnaireForm?.notaryCompleted;

                  const showFinalSubmit =
                    allFormsCompleted &&
                    (!questionnaireForm?.notaryRequired ||
                      questionnaireForm?.notaryCompleted);

                  return (
                    <div key={index}>
                      {showFinalSubmit && (
                        <div className="order-forms">
                          <div className="order-card final-card">
                            <div>
                              <div className="form-name">
                                <i
                                  className="bi bi-send-exclamation"
                                  style={{
                                    color: "grey",
                                    paddingRight: "10px",
                                  }}
                                ></i>
                                {needsToPay && "PAYMENT BEFORE "}FINAL
                                SUBMISSION
                              </div>
                              <div className="client-name">
                                All forms have been completed for{" "}
                                <b>
                                  {" "}
                                  {clients[order.clientID]?.diocese ||
                                    "Unknown Client"}
                                </b>
                                . Please{" "}
                                {needsToPay
                                  ? "pay to proceed."
                                  : "submit to proceed."}
                              </div>
                            </div>
                            <div className="form-status">
                              {needsToPay ? (
                                <button
                                  className="smbtn btn-green"
                                  onClick={() => handlePayment(order.orderID)}
                                >
                                  SUBMIT PAYMENT
                                </button>
                              ) : (
                                <button
                                  className="smbtn btn-green"
                                  onClick={() =>
                                    handleFinalSubmit(order.orderID)
                                  }
                                >
                                  SUBMIT
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {showNotaryRequirement && (
                        <div className="order-forms">
                          <div className="order-card notary-card">
                            <div>
                              <div className="form-name">
                                NOTARY REQUIREMENT
                              </div>
                              <div className="client-name">
                                {clients[order.clientID]?.diocese ||
                                  clients[order.clientID]?.clientName}{" "}
                                requires notarization of your completed
                                questionnaire.
                              </div>
                            </div>
                            <div className="form-status">
                              <button
                                className="smbtn btn-green"
                                onClick={() =>
                                  navigate("/candidate/forms/notary", {
                                    state: {
                                      orderID: order.orderID,
                                      orderFormID:
                                        questionnaireForm?.orderFormID,
                                    },
                                  })
                                }
                              >
                                START
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {!allFormsCompleted &&
                        orderForms[order.orderID]
                          ?.filter((form) => !form.completedFormUrl)
                          .map((form, formIndex) => (
                            <div key={formIndex} className="order-card">
                              <div>
                                <div className="form-name">
                                  {orderFormTypes[form.orderFormTypeID] ||
                                    "Unknown Form Type"}
                                </div>
                                <div className="client-name">
                                  {clients[order.clientID]?.clientName ||
                                    "Unknown Client"}{" "}
                                </div>
                              </div>
                              <div className="form-status">
                                <button
                                  className="smbtn btn-green"
                                  onClick={() =>
                                    handleStartForm(order.orderID, form)
                                  }
                                >
                                  START
                                </button>
                              </div>
                            </div>
                          ))}
                      {showPaymentModal && clientSecret && (
                        <Elements
                          options={{ clientSecret }}
                          stripe={stripePromise}
                        >
                          <PaymentForm
                            type="regular"
                            amount={175}
                            orderID={order.orderID}
                            onClose={() => {
                              setShowPaymentModal(false);
                            }}
                          />
                        </Elements>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sub-header">Nothing to Review.</div>
            )}
          </div>
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default NeedsReview;
